import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useSchoolId } from '../lib/auth/schoolContext';
import { Icons } from '../lib/Icons';
import AppLayout from '../components/AppLayout';

const RESOURCE_CATEGORIES = [
  { value: 'external', label: 'External Services (EP, SALT, OT, etc.)' },
  { value: 'equipment', label: 'Equipment & Resources' },
  { value: 'training', label: 'Training & CPD' },
];

const COST_TYPES = [
  { value: 'per_session', label: 'Per Session' },
  { value: 'per_term', label: 'Per Term' },
  { value: 'annual', label: 'Annual' },
];

export default function SchoolSettingsPage() {
  const navigate = useNavigate();
  const { schoolId } = useSchoolId();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: 'external',
    costType: 'per_session',
    cost: '',
    provider: '',
    notes: '',
  });

  // Fetch resources on mount
  useEffect(() => {
    const fetchResources = async () => {
      if (!schoolId) return;
      try {
        const q = query(collection(db, 'resourceCatalogue'), where('schoolId', '==', schoolId));
        const snapshot = await getDocs(q);
        const resourceList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setResources(resourceList);
      } catch (error) {
        console.error('Error fetching resources:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchResources();
  }, [schoolId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!schoolId) return;

    const resourceData = {
      ...formData,
      cost: parseFloat(formData.cost) || 0,
      schoolId,
      updatedAt: new Date(),
    };

    try {
      if (editingResource) {
        // Update existing
        await updateDoc(doc(db, 'resourceCatalogue', editingResource.id), resourceData);
        setResources(prev => prev.map(r => r.id === editingResource.id ? { ...r, ...resourceData } : r));
      } else {
        // Create new
        resourceData.createdAt = new Date();
        const docRef = await addDoc(collection(db, 'resourceCatalogue'), resourceData);
        setResources(prev => [...prev, { id: docRef.id, ...resourceData }]);
      }
      resetForm();
    } catch (error) {
      console.error('Error saving resource:', error);
      alert('Failed to save resource. Please try again.');
    }
  };

  const handleDelete = async (resourceId) => {
    if (!window.confirm('Are you sure you want to delete this resource?')) return;
    
    try {
      await deleteDoc(doc(db, 'resourceCatalogue', resourceId));
      setResources(prev => prev.filter(r => r.id !== resourceId));
    } catch (error) {
      console.error('Error deleting resource:', error);
      alert('Failed to delete resource. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: 'external',
      costType: 'per_session',
      cost: '',
      provider: '',
      notes: '',
    });
    setEditingResource(null);
    setShowAddModal(false);
  };

  const openEditModal = (resource) => {
    setFormData({
      name: resource.name || '',
      category: resource.category || 'external',
      costType: resource.costType || 'per_session',
      cost: resource.cost?.toString() || '',
      provider: resource.provider || '',
      notes: resource.notes || '',
    });
    setEditingResource(resource);
    setShowAddModal(true);
  };

  const getCostTypeLabel = (type) => {
    return COST_TYPES.find(t => t.value === type)?.label || type;
  };

  const getCategoryLabel = (category) => {
    return RESOURCE_CATEGORIES.find(c => c.value === category)?.label || category;
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto">
        {/* Back Navigation */}
        <button
          onClick={() => navigate('/dashboard')}
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-blue-600 mb-6 transition-colors group"
        >
          <Icons.ChevronLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
          Back to Dashboard
        </button>

        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-xl">
              <Icons.ChartBar className="h-7 w-7 text-blue-600" />
            </div>
            Provision Costs
          </h1>
          <p className="text-gray-600 mt-2">
            Manage costs for external services, equipment, and training
          </p>
        </div>

        {/* Resource Catalogue Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gray-50/50">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Resource Catalogue</h2>
              <p className="text-sm text-gray-600 mt-1">
                Manage costs for external services, equipment, and training for EHCNA costed provision maps.
              </p>
            </div>
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 shadow-sm transition-colors"
            >
              <Icons.Plus className="h-4 w-4" />
              Add Resource
            </button>
          </div>

          {/* Resources Table */}
          <div className="p-6">
            {resources.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                <div className="mx-auto w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-4">
                  <Icons.Folder className="h-6 w-6 text-blue-600" />
                </div>
                <p className="text-gray-900 font-medium">No resources added yet</p>
                <p className="text-sm text-gray-500 mt-1 max-w-sm mx-auto">
                  Add external services like EP, SALT, OT with their costs to use in provision maps.
                </p>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="mt-4 inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  <Icons.Plus className="h-4 w-4" />
                  Add your first resource
                </button>
              </div>
            ) : (
              <div className="overflow-hidden rounded-xl border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Name</th>
                      <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Category</th>
                      <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Cost</th>
                      <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Provider</th>
                      <th className="px-5 py-3.5 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {resources.map((resource) => (
                      <tr key={resource.id} className="hover:bg-blue-50/30 transition-colors">
                        <td className="px-5 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-gray-900">{resource.name}</span>
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                            {getCategoryLabel(resource.category)}
                          </span>
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap">
                          <span className="text-sm font-semibold text-gray-900">£{resource.cost?.toFixed(2) || '0.00'}</span>
                          <span className="text-xs text-gray-500 ml-1.5">({getCostTypeLabel(resource.costType)})</span>
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-600">{resource.provider || 'Not recorded'}</span>
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap text-right">
                          <button
                            onClick={() => openEditModal(resource)}
                            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm font-medium mr-4 transition-colors"
                          >
                            <Icons.Pencil className="h-3.5 w-3.5" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(resource.id)}
                            className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 text-sm font-medium transition-colors"
                          >
                            <Icons.Trash className="h-3.5 w-3.5" />
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Quick Reference */}
        <div className="mt-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-100">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
              <Icons.QuestionMarkCircle className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-blue-900">How costs are calculated</h3>
              <ul className="mt-2 text-sm text-blue-800 space-y-1.5">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0"></span>
                  <span><strong>Per Session</strong>: Cost × sessions per week × 39 weeks = Annual cost</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0"></span>
                  <span><strong>Per Term</strong>: Cost × 3 terms = Annual cost</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0"></span>
                  <span><strong>Annual</strong>: Direct annual cost</span>
                </li>
              </ul>
              <p className="mt-3 text-sm text-blue-700">
                Staff costs are calculated using hourly rates from the Staff Directory.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity" onClick={resetForm}></div>
            
            <div className="relative inline-block bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-6 py-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-blue-100 rounded-xl">
                      {editingResource ? (
                        <Icons.Pencil className="h-5 w-5 text-blue-600" />
                      ) : (
                        <Icons.Plus className="h-5 w-5 text-blue-600" />
                      )}
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">
                      {editingResource ? 'Edit Resource' : 'Add New Resource'}
                    </h3>
                  </div>
                  
                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Resource Name *</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition-colors"
                        placeholder="e.g. Educational Psychologist Assessment"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Category *</label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                        className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition-colors"
                      >
                        {RESOURCE_CATEGORIES.map(cat => (
                          <option key={cat.value} value={cat.value}>{cat.label}</option>
                        ))}
                      </select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Cost (£) *</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          required
                          value={formData.cost}
                          onChange={(e) => setFormData(prev => ({ ...prev, cost: e.target.value }))}
                          className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition-colors"
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Cost Type *</label>
                        <select
                          value={formData.costType}
                          onChange={(e) => setFormData(prev => ({ ...prev, costType: e.target.value }))}
                          className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition-colors"
                        >
                          {COST_TYPES.map(type => (
                            <option key={type.value} value={type.value}>{type.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Provider</label>
                      <input
                        type="text"
                        value={formData.provider}
                        onChange={(e) => setFormData(prev => ({ ...prev, provider: e.target.value }))}
                        className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition-colors"
                        placeholder="e.g. Local Authority, Private Practice"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Notes</label>
                      <textarea
                        rows={2}
                        value={formData.notes}
                        onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                        className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition-colors"
                        placeholder="Any additional notes..."
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="rounded-lg bg-white px-4 py-2.5 text-sm font-medium text-gray-700 border border-gray-300 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 shadow-sm transition-colors"
                  >
                    {editingResource ? 'Save Changes' : 'Add Resource'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
